/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import Form from "../ui/form/Form";
import { useForm } from "react-hook-form";
import FormGroup from "../ui/form/FormGroup";
import FormError from "../ui/form/FormError";
import PasswordInput from "../ui/form/PasswordInput";
import Button from "../ui/Button";
import Checkbox from "../ui/form/Checkbox";
import EmailField from "../ui/form/EmailField";
import FormFieldsWrapper from "../ui/form/FormFieldsWrapper";
import { useToast } from "../ui/Toast";
import { useAuth } from "../../hooks/useAuth";
import styled from "styled-components";
import Anchor from "../ui/Anchor";
import Text from "../ui/Text";
import T from "../ui/Translation";
import { ROUTES } from "../../constants/constants";
import { extractErrorMessage } from "../../utils/api/apiErrors";

const StyledForm = styled((props) => <Form {...props} />)`
    position: relative;
`;

const StyledFieldsFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
`;

const StyledRegistrationLink = styled((props) => <Text {...props} />)`
    text-align: center;
`;

const ClientLoginForm = ({ onSubmit, onForgotPasswordClick, showRegistrationLink }) => {
  const { toast } = useToast();
  const { login, isLoginPending } = useAuth();

  const {
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      remember_me: false,
    },
  });

  const handleLogin = async (data) => {
    const { email, password, remember_me } = data;

    try {
      const clientData = await login({ email, password, remember_me });
      onSubmit?.(clientData);
      toast({
        title: "success",
        description: "You have been successfully logged in",
        variant: "success",
      });
    } catch (error) {
      setError("root", { message: extractErrorMessage(error, "Something went wrong") });
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit(handleLogin)} noValidate>
      <FormFieldsWrapper>
        <FormGroup>
          <EmailField control={control} name="email" label="email" placeholder="enter_email" required={true} />
          {errors.email && <FormError name="email">{errors.email.message}</FormError>}
        </FormGroup>
        <FormGroup>
          <PasswordInput
            control={control}
            name="password"
            label="password"
            placeholder="enter_password"
            required={true}
          />
          {errors.password && <FormError name="password">{errors.password.message}</FormError>}
        </FormGroup>
        <StyledFieldsFooter>
          <FormGroup>
            <Checkbox control={control} name="remember_me">
              remember_login
            </Checkbox>
          </FormGroup>
          <Text size="small">
            <Anchor onClick={onForgotPasswordClick}>
              <T>remind_password</T>
            </Anchor>
          </Text>
        </StyledFieldsFooter>

      </FormFieldsWrapper>
      {errors.root && <FormError>{errors.root.message}</FormError>}
      <Button variant="primary" title="sign_in" loading={isLoginPending} fullWidth />

      {showRegistrationLink && (
        <StyledRegistrationLink size="small">
          <Anchor to={ROUTES.CLIENT_REGISTER}>
            <T>Don't have an account? Sign up</T>
          </Anchor>
        </StyledRegistrationLink>
      )}
    </StyledForm>
  );
};

export default ClientLoginForm;
